import {Config, Types, MatchAPI, RequestParams, Cookie} from '@trendads/common';
import * as CampaignAPI from './api/campaign';
import * as CreativeAPI from '@trendads/common/dist/creative-management/RemoteAPI';

export async function resolvePath(path: string) {
    return await import('./templates/' + path);
}

const API_HOST = (() => {
    // if (window.location.hostname === 'localhost') return 'https://neo.bet';
    if (window.location.hostname === 'localhost') return 'https://banner-api.test.reactivebetting.com';
    switch (window.location.hostname) {
        case 'banner.neobet.de':
            return 'https://banner-api.neobet.de';
        case 'banner-on.neobet.ca':
            return 'https://banner-api.neobet.ca';
        case 'banner.neo.bet':
            return 'https://banner-api.neo.bet';
        case 'banner.neobet.co.za':
            return 'https://banner-api.neobet.co.za';
        case 'banner-test.reactivebetting.de':
            return 'https://banner-api.test.reactivebetting.de';
        case 'banner-on-test.reactivebetting.com':
            return 'https://banner-api-on.test.reactivebetting.com';
        case 'banner-test.reactivebetting.com':
            return 'https://banner-api.test.reactivebetting.com';
        case 'banner-za-test.reactivebetting.com':
            return 'https://banner-api-za.test.reactivebetting.com';
        default:
            return 'https://banner-api.test.reactivebetting.com';
    }
})();

const LINK_TARGET = (() => {
    // if (window.location.hostname === 'localhost') return 'https://neo.bet';
    if (window.location.hostname === 'localhost') return 'https://test.reactivebetting.com';
    switch (window.location.hostname) {
        case 'banner.neobet.de':
            return 'https://neobet.de';
        case 'banner-on.neobet.ca':
            return 'https://on.neobet.ca';
        case 'banner.neo.bet':
            return 'https://neo.bet';
        case 'banner.neobet.co.za':
            return 'https://neobet.co.za';
        case 'banner-test.reactivebetting.de':
            return 'https://test.reactivebetting.de';
        case 'banner-on-test.reactivebetting.com':
            return 'https://on-test.reactivebetting.com';
        case 'banner-test.reactivebetting.com':
            return 'https://test.reactivebetting.com';
        case 'banner-za-test.reactivebetting.com':
            return 'https://za-test.reactivebetting.com';
        default:
            return 'https://test.reactivebetting.com';
    }
})();

/** Parent domain for BannerAffiliateId so the main site can read it (cannot derive with slice(1): neo.bet → "bet", neobet.co.za → "co.za"). */
const affiliateIdCookieDomainByLinkHost: Record<string, string> = {
    'test.reactivebetting.com': 'reactivebetting.com',
    'test.reactivebetting.de': 'reactivebetting.de',
    'on-test.reactivebetting.com': 'reactivebetting.com',
    'za-test.reactivebetting.com': 'reactivebetting.com',
    'neo.bet': 'neo.bet',
    'neobet.de': 'neobet.de',
    'on.neobet.ca': 'neobet.ca',
    'neobet.co.za': 'neobet.co.za'
};

const licensesByHostname = {
    [Types.License.DE]: ['banner.neobet.de', 'banner-test.reactivebetting.de'],
    [Types.License['CA-ON']]: ['banner-on.neobet.ca', 'banner-on-test.reactivebetting.com'],
    [Types.License.MT]: ['banner.neo.bet', 'banner-test.reactivebetting.com'],
    [Types.License.ZA]: ['banner.neobet.co.za', 'banner-za-test.reactivebetting.com']
};

const CONFIG: Config.Config = {
    customer: 'neobet',
    apiHost: API_HOST,
    creativeApiHost: 'https://creative-api.reactivebetting.com',
    linkTarget: LINK_TARGET,
    trackingEnabled: true,
    supportedTemplates: [Types.Template.MatchBanner],
    supportedSlideTypes: [Types.SlideType.Match, Types.SlideType.Campaign, Types.SlideType.Creative],
    supportedThemes: [Types.Theme.Bright, Types.Theme.Dark],
    supportedLanguages: [Types.Language.en, Types.Language.de, Types.Language.fi],
    trendsFallbackLanguages: {},
    supportedCountries: Object.keys(Types.Country) as Types.Country[],
    supportedCurrencies: [Types.Currency.EUR, Types.Currency.USD, Types.Currency.CAD],
    supportedOddsFormats: [Types.OddsFormat.Decimal, Types.OddsFormat.American, Types.OddsFormat.Fractional],
    supportedSports: Object.keys(MatchAPI.Sport) as MatchAPI.Sport[],
    supportUrlContextAnalysis: true,
    supportedLicenses: [Types.License.MT, Types.License.DE, Types.License['CA-ON'], Types.License.ZA],
    defaultLicense: Types.License.MT,
    lazyLoading: true,
    tcf: {
        enabled: true,
        timeoutMs: 1000,
        disclosures: {
            BannerToken: {
                purposeConsentIds: [1, 3, 4, 8]
            },
            // UserToken: {
            //     purposeConsentIds: [1, 7, 9]
            // },
            BannerAffiliateId: {
                purposeConsentIds: [1, 7]
            },
            REST_API_Tracking: {
                purposeConsentIds: [1, 7, 9]
            }
        },
        vendorId: 1581,
        defaultConsent: true,
        debug: true
    },
    defaults: {
        template: Types.Template.MatchBanner,
        theme: Types.Theme.Dark,
        language: Types.Language.en,
        oddsFormat: Types.OddsFormat.Decimal,
        currency: Types.Currency.EUR,
        bettingType: MatchAPI.BettingType.MatchWin,
        updateInterval: 60 * 1000,
        matchCount: 5,
        campaignCount: 0,
        stake: 10,
        sortBy: MatchAPI.SortBy.Popularity
    },
    getLicense: (config: Config.Config) => {
        let licenseByUrl: Types.License | undefined;
        if (window.location.hostname === 'localhost') {
            const searchParams = new URLSearchParams(window.location.search);
            licenseByUrl = (searchParams.get('license')?.toUpperCase() ?? undefined) as Types.License | undefined;
        } else {
            licenseByUrl = Object.entries(licensesByHostname).find(([, hostnames]) => {
                return hostnames.includes(window.location.hostname);
            })?.[0] as Types.License | undefined;
        }

        if (licenseByUrl && config.supportedLicenses.includes(licenseByUrl)) {
            return licenseByUrl;
        } else {
            return config.defaultLicense;
        }
    },
    changeLicense: (license: Types.License) => {
        if (window.location.hostname === 'localhost') {
            const url = new URL(window.location.href);
            url.searchParams.set('license', license);
            window.location.href = url.toString();
        } else {
            const currentLicenseHostnameIndex = Object.entries(licensesByHostname)
                .find(([, hostnames]) => hostnames.includes(window.location.hostname))?.[1]
                .indexOf(window.location.hostname);

            if (currentLicenseHostnameIndex === undefined || currentLicenseHostnameIndex === -1) return;

            const newLicenseHostname = licensesByHostname[license][currentLicenseHostnameIndex];
            const url = new URL(window.location.href);
            url.hostname = newLicenseHostname;
            window.location.href = url.toString();
        }
    },
    getSupportedBettingTypesByTemplate: (template: Types.Template): MatchAPI.BettingType[] => {
        switch (template) {
            case Types.Template.MatchBanner:
                return [
                    MatchAPI.BettingType.MatchWin,
                    MatchAPI.BettingType.OddsBoost,
                    // MatchAPI.BettingType.OverUnder,
                    MatchAPI.BettingType.Spread,
                    MatchAPI.BettingType.Handicap
                ];
            default:
                return [];
        }
    },
    getSupportedSizesByTemplate: (template: Types.Template): Types.Size[] => {
        switch (template) {
            case Types.Template.MatchBanner:
                return [
                    Types.Size['120x240'],
                    Types.Size['120x600'],
                    Types.Size['160x600'],
                    Types.Size['200x200'],
                    Types.Size['300x600'],
                    Types.Size['300x250'],
                    Types.Size['320x50'],
                    Types.Size['320x75'],
                    Types.Size['320x150'],
                    Types.Size['320x320'],
                    Types.Size['320x480'],
                    Types.Size['350x120'],
                    Types.Size['360x80'],
                    Types.Size['360x120'],
                    Types.Size['384x50'],
                    Types.Size['392x50'],
                    Types.Size['360x50'],
                    Types.Size['400x600'],
                    Types.Size['411x50'],
                    Types.Size['450x125'],
                    Types.Size['468x60'],
                    Types.Size['600x600'],
                    Types.Size['600x1200'],
                    Types.Size['640x100'],
                    Types.Size['640x120'],
                    Types.Size['640x130'],
                    Types.Size['640x150'],
                    Types.Size['640x300'],
                    Types.Size['640x320'],
                    Types.Size['680x100'],
                    Types.Size['680x1200'],
                    Types.Size['700x110'],
                    Types.Size['728x90'],
                    Types.Size['800x250'],
                    Types.Size['900x150'],
                    Types.Size['970x90'],
                    Types.Size['970x250'],
                    Types.Size['1400x220'],
                    Types.Size['1600x150'],
                    Types.Size['1920x1080'],
                    Types.Size.API
                ];
            default:
                return [];
        }
    },

    getSupportedCreativeSizes: (): Types.Size[] => {
        return [
            Types.Size['120x240'],
            Types.Size['120x600'],
            Types.Size['160x600'],
            Types.Size['200x200'],
            Types.Size['300x600'],
            Types.Size['300x250'],
            Types.Size['320x50'],
            Types.Size['320x75'],
            Types.Size['320x150'],
            Types.Size['320x320'],
            Types.Size['320x480'],
            Types.Size['350x120'],
            Types.Size['360x80'],
            Types.Size['360x120'],
            Types.Size['384x50'],
            Types.Size['392x50'],
            Types.Size['360x50'],
            Types.Size['400x600'],
            Types.Size['411x50'],
            Types.Size['450x125'],
            Types.Size['468x60'],
            Types.Size['600x600'],
            Types.Size['600x1200'],
            Types.Size['640x100'],
            Types.Size['640x120'],
            Types.Size['640x130'],
            Types.Size['640x150'],
            Types.Size['640x300'],
            Types.Size['640x320'],
            Types.Size['680x100'],
            Types.Size['680x1200'],
            Types.Size['700x110'],
            Types.Size['728x90'],
            Types.Size['800x250'],
            Types.Size['900x150'],
            Types.Size['970x90'],
            Types.Size['970x250'],
            Types.Size['1400x220'],
            Types.Size['1600x150'],
            Types.Size['1920x1080'],
            Types.Size['1596x367'],
            Types.Size['768x245'],
            Types.Size['140x200'],
            Types.Size['280x200'],
            Types.Size['640x1138'],
            Types.Size['540x600']
        ];
    },

    getMaxMatchCountPerSlide: (template: Types.Template, size: Types.Size): number => {
        switch (template) {
            default:
                switch (size) {
                    default:
                        return 1;
                }
        }
    },

    resolvePath,

    loadI18n: async (language: Types.Language): Promise<Types.I18nData> => {
        return (await import(`./data/i18n/${language}.json`)).default;
    },

    loadImage: async (path: string): Promise<string> => {
        return (await import(`./data/${path}`)).default;
    },

    loadCreatives: async (config: Config.Config): Promise<string[]> => {
        return CreativeAPI.getCreativeIds(config);
    },

    loadCampaigns: async (
        campaignCount: number,
        requestParams: RequestParams.RequestParams,
        config: Config.Config
    ): Promise<Types.Campaign[]> => {
        const [error, campaigns] = await CampaignAPI.getCampaigns(API_HOST, {
            campaignCount: String(campaignCount),
            country: requestParams.country?.toString(),
            affiliateId: requestParams.affiliateId,
            customerId: requestParams.customerId
        });

        if (error || !campaigns) {
            if (error) console.error(error);
            return [];
        }

        return campaigns.map(campaign => ({
            creativeId: campaign.creativeId,
            id: campaign.campaignId,
            link: campaign.link
        }));
    },

    getCustomerId: () => Cookie.getCookieValue('BannerToken'),

    getCustomerBetslipSelection: () => {
        const raw = Cookie.getCookieValue('CurrentBetslipSelection');
        if (!raw) return undefined;
        const ids = raw
            .split(',')
            .map(id => id.trim())
            .filter(Boolean);
        return ids.length > 0 ? ids : undefined;
    },

    setAffiliateCookie: (affiliateId: string) => {
        const domain =
            window.location.hostname === 'localhost'
                ? undefined
                : affiliateIdCookieDomainByLinkHost[new URL(LINK_TARGET).hostname];

        Cookie.setCookieValue('BannerAffiliateId', `${affiliateId}|${Date.now()}`, {
            path: '/',
            domain,
            maxAge: 30 * 24 * 60 * 60,
            secure: true,
            sameSite: 'None'
        });
    },

    loadCreative: async (
        id: string,
        requestParams: RequestParams.RequestParams,
        config: Config.Config
    ): Promise<string | null> => {
        const size = requestParams.size;
        if (!size) return null;

        return CreativeAPI.getCreative(config, id, requestParams.language, size);
    },

    fallbackCreativeId: 'livequotenboosts',

    getDefaultSlides: language => ({before: [], after: []}),

    creativeManagement: {
        authClientId: '1036749859651-7gi8f941h5djv3e435vk6rf9rfdski9b.apps.googleusercontent.com',
        awsRole: 'arn:aws:iam::801402587766:role/BannerUpdateRole'
    }
};

export default CONFIG;
